
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Bell, Heart, Settings, LogOut, Info, Copy, Check } from 'lucide-react';
import { getUserInfo } from '@/utils/jwtUtils';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import packageJson from '../../package.json';

interface HeaderSectionProps {
  onTitleClick: () => void;
  onLogout: () => void;
}

export const HeaderSection = ({ onTitleClick, onLogout }: HeaderSectionProps) => {
  const [showAccountInfo, setShowAccountInfo] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const { toast } = useToast();
  const userInfo = getUserInfo();
  const displayName = userInfo?.given_name || 'User';

  // App uses Google (Gmail) sign-in only
  const getProvider = (_sub?: string): string => {
    return 'Google';
  };

  const provider = getProvider(userInfo?.sub);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(label);
    setTimeout(() => setCopiedField(null), 2000);
    toast({
      title: "Copied!",
      description: `${label} copied to clipboard`,
    });
  };

  return (
    <header className="border-b bg-white/95 backdrop-blur-sm sticky top-0 z-40">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Heart className="h-6 w-6 text-primary" />
              <button 
                onClick={onTitleClick}
                className="text-xl font-bold text-primary hover:text-primary/80 transition-colors cursor-pointer"
              >
                healthscores.ai
              </button>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm">
              <Bell className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm">
              <Settings className="h-4 w-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center space-x-2 hover:bg-accent px-2 sm:px-3">
                  <Avatar className="h-8 w-8 flex-shrink-0">
                    <AvatarImage src={userInfo?.picture} alt={displayName} />
                    <AvatarFallback>
                      {displayName.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm font-medium">{displayName}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-2 py-2">
                    <p className="text-sm font-medium leading-none">{userInfo?.name || displayName}</p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {userInfo?.email || 'No email available'}
                    </p>
                    <div className="flex items-center gap-2 pt-1">
                      <Badge variant="secondary" className="text-xs">
                        {provider}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        v{packageJson.version}
                      </Badge>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowAccountInfo(true)} className="cursor-pointer">
                  <Info className="h-4 w-4 mr-2" />
                  Account Info
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} className="cursor-pointer text-red-600 focus:text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Account Info Dialog */}
      <Dialog open={showAccountInfo} onOpenChange={setShowAccountInfo}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Account Information</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 sm:space-y-4">
            {/* User Details */}
            <div className="space-y-2 sm:space-y-3">
              <h3 className="text-xs sm:text-sm font-semibold text-muted-foreground uppercase">User Details</h3>

              <div className="grid grid-cols-1 gap-2 sm:gap-3">
                <div className="flex items-center justify-between p-2 sm:p-3 rounded-lg border bg-muted/50">
                  <div className="flex-1 min-w-0 pr-2">
                    <p className="text-xs text-muted-foreground mb-1">Full Name</p>
                    <p className="text-xs sm:text-sm font-medium truncate">{userInfo?.name || 'Not available'}</p>
                  </div>
                  {userInfo?.name && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-shrink-0"
                      onClick={() => copyToClipboard(userInfo.name!, 'Name')}
                    >
                      {copiedField === 'Name' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  )}
                </div>

                <div className="flex items-center justify-between p-2 sm:p-3 rounded-lg border bg-muted/50">
                  <div className="flex-1 min-w-0 pr-2">
                    <p className="text-xs text-muted-foreground mb-1">Email</p>
                    <p className="text-xs sm:text-sm font-medium break-all">{userInfo?.email || 'Not available'}</p>
                  </div>
                  {userInfo?.email && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-shrink-0"
                      onClick={() => copyToClipboard(userInfo.email!, 'Email')}
                    >
                      {copiedField === 'Email' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Authentication Details */}
            <div className="space-y-2 sm:space-y-3">
              <h3 className="text-xs sm:text-sm font-semibold text-muted-foreground uppercase">Authentication</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                <div className="p-2 sm:p-3 rounded-lg border bg-muted/50">
                  <p className="text-xs text-muted-foreground mb-1">SSO Provider</p>
                  <Badge variant="secondary" className="text-xs sm:text-sm">{provider}</Badge>
                </div>

                <div className="p-2 sm:p-3 rounded-lg border bg-muted/50">
                  <p className="text-xs text-muted-foreground mb-1">Session Status</p>
                  <Badge variant="default" className="text-xs sm:text-sm bg-green-600">Active</Badge>
                </div>
              </div>
            </div>

            {/* App Information */}
            <div className="space-y-2 sm:space-y-3">
              <h3 className="text-xs sm:text-sm font-semibold text-muted-foreground uppercase">Application</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                <div className="p-2 sm:p-3 rounded-lg border bg-muted/50">
                  <p className="text-xs text-muted-foreground mb-1">Version</p>
                  <p className="text-xs sm:text-sm font-medium">v{packageJson.version}</p>
                </div>

                <div className="p-2 sm:p-3 rounded-lg border bg-muted/50">
                  <p className="text-xs text-muted-foreground mb-1">Environment</p>
                  <p className="text-xs sm:text-sm font-medium">{import.meta.env.MODE || 'production'}</p>
                </div>
              </div>
            </div>

            {/* Support Information */}
            <div className="p-3 sm:p-4 rounded-lg bg-blue-50 border border-blue-200">
              <p className="text-xs text-blue-800 font-medium mb-2">For Support</p>
              <p className="text-xs text-blue-600 mb-2">
                Need help? Contact our support team for assistance.
              </p>
              <a
                href="mailto:support@zpaper.com"
                className="text-xs text-blue-700 font-medium hover:underline"
              >
                support@zpaper.com
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
};
